import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Stack, alpha } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import HeroBg from '../../assets/background.jpg'; 

interface TextSectionProps {
  isDark: boolean;
}

const TextSection: React.FC<TextSectionProps> = ({ isDark }) => {
  const navigate = useNavigate();
  const [drinkIndex, setDrinkIndex] = useState(0);
  const drinks = ['coffee', 'chai', 'croissant', 'cappuccino', 'americano', 'latte', 'matcha', 'espresso'];

  // Theme constants
  const lokiTextGradient = 'linear-gradient(45deg, #C4B66F 10%, #44A08D 90%)'; 
  const lightModeTextGradient = 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)';
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setDrinkIndex((prev) => (prev + 1) % drinks.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [drinks.length]);

  return (
    <Box
      sx={{
        minHeight: '100vh', // Slightly reduced height
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundImage: `url(${HeroBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: isDark 
            ? 'rgba(5, 24, 20, 0.7)' 
            : 'rgba(255, 255, 255, 0.65)',
          backdropFilter: isDark ? 'blur(2px)' : 'none',
          zIndex: 0
        }
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ 
          textAlign: 'center', 
          py: { xs: 6, md: 10 }, // Reduced padding
          position: 'relative',
          zIndex: 1,
        }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5.5rem' }, // Slightly smaller
              lineHeight: 0.95,
              mb: { xs: 1, sm: 6 },
              textTransform: 'uppercase',
              ...discordFont,
              background: isDark ? 'none' : lightModeTextGradient,
              WebkitBackgroundClip: isDark ? 'none' : 'text',
              WebkitTextFillColor: isDark ? 'white' : 'transparent',
              textShadow: isDark ? '0 0 30px rgba(12, 59, 46, 0.5)' : 'none',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: { xs: '-30px', sm: '-20px' },
                left: '50%',
                transform: 'translateX(-50%)',
                width: { xs: '150px', sm: '200px' },
                height: '4px',
                background: lokiTextGradient, 
                borderRadius: '2px',
                display: { xs: 'none', sm: 'block' },
              }
            }}
          >
            Your office is where
            <Box 
              component="span" 
              sx={{ 
                display: 'block',
                background: lokiTextGradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                position: 'relative',
                minHeight: { xs: '1.8em', sm: '1.2em' },
                fontStyle: 'italic',
                mt: { xs: 1, sm: 0 },
                lineHeight: { xs: 1.2, sm: 0.95 },
              }}
            >
              your {drinks[drinkIndex]} is.
            </Box>
          </Typography>

          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="center"
            alignItems="center"
            sx={{ position: 'relative', zIndex: 1, mt: { xs: 2, sm: 8 }, flexWrap: 'nowrap' }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/explore')}
              endIcon={<ArrowForward sx={{ fontSize: { xs: '1rem', md: '1.5rem' } }} />}
              sx={{
                minWidth: { xs: 'auto', sm: '200px' },
                px: { xs: 2, sm: 5 },
                py: { xs: 1.5, sm: 2 },
                borderRadius: '100px',
                background: isDark 
                  ? `linear-gradient(135deg, rgba(5, 24, 20, 0.8) 0%, rgba(15, 59, 46, 0.8) 100%)`
                  : lightModeTextGradient,
                color: isDark ? '#C4B66F' : 'white', 
                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                textTransform: 'none',
                backdropFilter: 'blur(10px)',
                border: isDark ? '1px solid rgba(196, 182, 111, 0.3)' : 'none',
                ...discordFont,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 10px 20px ${alpha('#0C3B2E', 0.5)}`,
                },
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}
            >
              Find Cafes
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/community')}
              sx={{
                minWidth: { xs: 'auto', sm: '200px' },
                px: { xs: 2, sm: 5 },
                py: { xs: 1.5, sm: 2 },
                borderRadius: '100px',
                border: isDark ? `1px solid rgba(196, 182, 111, 0.3)` : `2px solid #051814`,
                color: isDark ? 'white' : '#051814',
                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                textTransform: 'none',
                backdropFilter: 'blur(10px)',
                bgcolor: alpha(isDark ? '#1A1A1A' : '#FFF', 0.2),
                ...discordFont,
                '&:hover': {
                  border: isDark ? `1px solid #C4B66F` : `2px solid #0F3B2E`,
                  bgcolor: alpha('#0C3B2E', 0.1),
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}
            >
              Join Community
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default TextSection;